/**
 * NEXUS UI - QueueVisualizer
 * Visual representation of jobs/tasks in a processing queue
 */

import React from 'react';
import type { BaseProps, NexusVariant } from './types';

type JobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'paused';

interface QueueJob {
  id: string;
  name: string;
  status: JobStatus;
  progress?: number; // 0-100 for processing jobs
  priority?: 'low' | 'normal' | 'high' | 'critical';
  startedAt?: string;
  duration?: string;
}

export interface QueueVisualizerProps extends BaseProps {
  jobs: QueueJob[];
  variant?: NexusVariant;
  maxVisible?: number;
  orientation?: 'horizontal' | 'vertical';
  showProgress?: boolean;
  showPriority?: boolean;
  compact?: boolean;
  onJobClick?: (job: QueueJob) => void;
}

const statusConfig: Record<JobStatus, { color: string; glow: string; label: string; icon: string }> = {
  pending: { color: '#6e7681', glow: 'transparent', label: 'Pending', icon: '○' },
  processing: { color: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)', label: 'Processing', icon: '◐' },
  completed: { color: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)', label: 'Done', icon: '●' },
  failed: { color: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)', label: 'Failed', icon: '✕' },
  paused: { color: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)', label: 'Paused', icon: '❚❚' },
};

const priorityConfig: Record<string, { color: string; label: string }> = {
  low: { color: 'var(--nx-text-ghost)', label: 'LOW' },
  normal: { color: 'var(--nx-text-tertiary)', label: 'NORM' },
  high: { color: '#ffaa00', label: 'HIGH' },
  critical: { color: '#ff3366', label: 'CRIT' },
};

export const QueueVisualizer: React.FC<QueueVisualizerProps> = ({
  jobs,
  maxVisible = 10,
  orientation = 'vertical',
  showProgress = true,
  showPriority = true,
  compact = false,
  onJobClick,
  className = '',
  style,
}) => {
  const visibleJobs = jobs.slice(0, maxVisible);
  const hiddenCount = Math.max(0, jobs.length - maxVisible);
  const isHorizontal = orientation === 'horizontal';

  // Calculate stats
  const stats = {
    total: jobs.length,
    pending: jobs.filter(j => j.status === 'pending').length,
    processing: jobs.filter(j => j.status === 'processing').length,
    completed: jobs.filter(j => j.status === 'completed').length,
    failed: jobs.filter(j => j.status === 'failed').length,
  };

  return (
    <div
      className={`nx-queue-visualizer ${className}`}
      style={{
        background: 'var(--nx-glass-dark)',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '4px',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: compact ? '8px 12px' : '12px 16px',
          background: 'var(--nx-void-elevated)',
          borderBottom: '1px solid rgba(0, 212, 255, 0.1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              fontFamily: 'var(--nx-font-interface)',
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--nx-text-tertiary)',
            }}
          >
            Queue
          </span>
          <span
            style={{
              fontFamily: 'var(--nx-font-mono)',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--nx-text-primary)',
            }}
          >
            {stats.total}
          </span>
        </div>

        {/* Mini stats */}
        <div style={{ display: 'flex', gap: '12px' }}>
          {stats.processing > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: statusConfig.processing.color, fontSize: '10px' }}>●</span>
              <span style={{ fontFamily: 'var(--nx-font-mono)', fontSize: '10px', color: 'var(--nx-text-tertiary)' }}>
                {stats.processing}
              </span>
            </span>
          )}
          {stats.pending > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: statusConfig.pending.color, fontSize: '10px' }}>○</span>
              <span style={{ fontFamily: 'var(--nx-font-mono)', fontSize: '10px', color: 'var(--nx-text-tertiary)' }}>
                {stats.pending}
              </span>
            </span>
          )}
          {stats.failed > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: statusConfig.failed.color, fontSize: '10px' }}>✕</span>
              <span style={{ fontFamily: 'var(--nx-font-mono)', fontSize: '10px', color: 'var(--nx-text-tertiary)' }}>
                {stats.failed}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Jobs list */}
      <div
        style={{
          display: 'flex',
          flexDirection: isHorizontal ? 'row' : 'column',
          gap: compact ? '4px' : '8px',
          padding: compact ? '8px' : '12px',
          overflowX: isHorizontal ? 'auto' : 'hidden',
          overflowY: isHorizontal ? 'hidden' : 'auto',
          maxHeight: isHorizontal ? 'auto' : '300px',
        }}
      >
        {visibleJobs.length === 0 ? (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              color: 'var(--nx-text-ghost)',
              fontFamily: 'var(--nx-font-interface)',
              fontSize: '12px',
              width: '100%',
            }}
          >
            Queue empty
          </div>
        ) : (
          visibleJobs.map((job) => {
            const status = statusConfig[job.status];
            const priority = job.priority ? priorityConfig[job.priority] : null;

            return (
              <div
                key={job.id}
                onClick={() => onJobClick?.(job)}
                style={{
                  display: 'flex',
                  flexDirection: isHorizontal ? 'column' : 'row',
                  alignItems: isHorizontal ? 'center' : 'flex-start',
                  gap: compact ? '6px' : '10px',
                  padding: compact ? '8px 10px' : '10px 14px',
                  background: 'var(--nx-void-panel)',
                  border: `1px solid ${job.status === 'processing' ? status.color : 'rgba(255, 255, 255, 0.05)'}`,
                  borderRadius: '4px',
                  minWidth: isHorizontal ? '120px' : 'auto',
                  cursor: onJobClick ? 'pointer' : 'default',
                  transition: 'all 0.15s ease-out',
                  boxShadow: job.status === 'processing' ? `0 0 12px ${status.glow}` : 'none',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'var(--nx-void-elevated)';
                  e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'var(--nx-void-panel)';
                  e.currentTarget.style.borderColor = job.status === 'processing' ? status.color : 'rgba(255, 255, 255, 0.05)';
                }}
              >
                {/* Status indicator */}
                <div
                  style={{
                    flexShrink: 0,
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: status.color,
                    animation: job.status === 'processing' ? 'nx-rotate 1.5s linear infinite' : 'none',
                  }}
                >
                  {status.icon}
                </div>

                {/* Job info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: showProgress && job.status === 'processing' ? '6px' : 0,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--nx-font-interface)',
                        fontSize: compact ? '11px' : '12px',
                        color: 'var(--nx-text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {job.name}
                    </span>

                    {showPriority && priority && job.priority !== 'normal' && (
                      <span
                        style={{
                          fontFamily: 'var(--nx-font-mono)',
                          fontSize: '8px',
                          fontWeight: 600,
                          color: priority.color,
                          padding: '1px 4px',
                          background: `${priority.color}20`,
                          borderRadius: '2px',
                        }}
                      >
                        {priority.label}
                      </span>
                    )}
                  </div>

                  {/* Progress bar for processing jobs */}
                  {showProgress && job.status === 'processing' && job.progress !== undefined && (
                    <div
                      style={{
                        height: '3px',
                        background: 'var(--nx-void-deep)',
                        borderRadius: '2px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${job.progress}%`,
                          height: '100%',
                          background: status.color,
                          boxShadow: `0 0 8px ${status.glow}`,
                          transition: 'width 0.3s ease-out',
                        }}
                      />
                    </div>
                  )}

                  {/* Duration/time info */}
                  {job.duration && !compact && (
                    <span
                      style={{
                        fontFamily: 'var(--nx-font-mono)',
                        fontSize: '9px',
                        color: 'var(--nx-text-ghost)',
                        marginTop: '4px',
                        display: 'block',
                      }}
                    >
                      {job.duration}
                    </span>
                  )}
                </div>

                {/* Progress percentage for horizontal layout */}
                {isHorizontal && job.status === 'processing' && job.progress !== undefined && (
                  <span
                    style={{
                      fontFamily: 'var(--nx-font-mono)',
                      fontSize: '10px',
                      fontWeight: 600,
                      color: status.color,
                    }}
                  >
                    {job.progress}%
                  </span>
                )}
              </div>
            );
          })
        )}

        {/* Hidden count indicator */}
        {hiddenCount > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              color: 'var(--nx-text-ghost)',
              fontFamily: 'var(--nx-font-mono)',
              fontSize: '11px',
              minWidth: isHorizontal ? '80px' : 'auto',
            }}
          >
            +{hiddenCount} more
          </div>
        )}
      </div>
    </div>
  );
};

export default QueueVisualizer;
